import { Spinner } from "../ui";
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { authService } from "../../services/auth.service";
import { AuthResponse, AuthContextType, LoginCredentials } from "../../types/auth";
import { isAdminAllowedRole } from "../../utils/authRole";

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<AuthResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const normalizeUser = (currentUser: AuthResponse | null): AuthResponse | null => {
        if (!currentUser) {
            return null;
        }

        if (!isAdminAllowedRole(currentUser.tokenRole ?? currentUser.modules ?? null)) {
            authService.clearSession();
            return null;
        }

        return currentUser;
    };

    useEffect(() => {
        const initializeAuth = () => {
            const storedUser = authService.getUserData();
            if (storedUser) {
                setUser(normalizeUser(storedUser));
            }
            setIsLoading(false);
        };

        initializeAuth();
    }, []);

    const login = async (credentials: LoginCredentials) => {
        const response = await authService.login(credentials);
        setUser(normalizeUser(response));
    };

    const logout = () => {
        authService.logout();
        setUser(null);
    };

    /**
     * The new backend uses module IDs instead of fine-grained permissions.
     * Always returns true for authenticated admin users (module access was
     * already validated at login time).
     */
    const hasPermission = (_permission: string) => !!user;

    /**
     * With the new module-based backend, role checks are mapped:
     * if the user has tokenRole "Admin" (set during login when module "1"
     * is present), any role list containing "Admin" will match.
     */
    const hasRole = (roles: string[] = ["Admin"]) => {
        if (!user?.tokenRole) {
            return false;
        }
        return roles.some((role) => role.toLowerCase() === user.tokenRole?.toLowerCase());
    };

    if (isLoading) {
        return (
            <div className="bg-canvas flex min-h-dvh items-center justify-center">
                <Spinner size="size-8" label="Loading" />
            </div>
        );
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user,
                isAuthorizedRole: isAdminAllowedRole(user?.tokenRole ?? user?.modules ?? null),
                login,
                logout,
                hasPermission,
                hasRole,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within an AuthProvider");
    return context;
};

/**
 * Same as useAuth but guarantees user is not null.
 * Use inside any authenticated route – no more null checks.
 */
export const useRequiredAuth = () => {
    const auth = useAuth();
    if (!auth.user) throw new Error("useRequiredAuth used without a logged-in user");
    return { ...auth, user: auth.user, userId: auth.user.userId };
};

